import { initializeApp } from 'firebase/app';
import { 
  initializeFirestore, 
  persistentLocalCache, 
  persistentMultipleTabManager,
  memoryLocalCache
} from 'firebase/firestore';
import { getAuth, setPersistence, browserLocalPersistence, indexedDBLocalPersistence } from 'firebase/auth';
import { getAnalytics } from 'firebase/analytics';
import { getStorage } from 'firebase/storage';
import { getMessaging, isSupported } from 'firebase/messaging';
import { Capacitor } from '@capacitor/core';

// Firebase config - using environment variables
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY || "demo-api-key",
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || "demo.firebaseapp.com",
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || "demo-project",
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || "demo.appspot.com",
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || "123456789",
  appId: process.env.REACT_APP_FIREBASE_APP_ID || "1:123456789:web:demo",
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID || "G-DEMO"
};

let app, db, auth, analytics, storage, messaging;

try {
  const isValidConfig = firebaseConfig.apiKey &&
    firebaseConfig.projectId;

  if (isValidConfig) {
    // Initialize Firebase
    app = initializeApp(firebaseConfig);

    // Initialize Firestore with Modern Local Cache with fallback to memoryLocalCache
    try {
      db = initializeFirestore(app, {
        cache: persistentLocalCache({
          tabManager: persistentMultipleTabManager()
        })
      });
    } catch (cacheErr) {
      console.warn('⚠️ Firestore persistent cache failed, falling back to memory cache:', cacheErr?.message || cacheErr);
      try {
        db = initializeFirestore(app, {
          cache: memoryLocalCache()
        });
      } catch (memErr) {
        console.warn('⚠️ Firestore memory cache init warning:', memErr?.message || memErr);
      }
    }

    auth = getAuth(app);
    auth.languageCode = 'tr'; // 🌐 Set language to Turkish
    
    // 🛡️ AUTH PERSISTENCE: Mobil (Capacitor) için indexedDB, web için browserLocal
    const persistenceMode = Capacitor.isNativePlatform() ? indexedDBLocalPersistence : browserLocalPersistence;
    setPersistence(auth, persistenceMode).catch(err => {
      console.warn('⚠️ Auth persistence setting failed:', err.message);
    });

    storage = getStorage(app);

    // Web FCM Messaging (Native ortamda Capacitor Push Notifications kullanılır)
    messaging = null;
    if (!Capacitor.isNativePlatform() && typeof window !== 'undefined') {
      try {
        isSupported().then(supported => {
          if (supported) {
            try {
              messaging = getMessaging(app);
            } catch (mErr) {
              console.warn('⚠️ FCM getMessaging failed:', mErr.message);
            }
          } else {
            console.log('ℹ️ Firebase Messaging is not supported in this browser environment.');
          }
        }).catch(err => {
          console.warn('⚠️ FCM isSupported check failed:', err?.message || err);
        });
      } catch (err) {
        console.warn('⚠️ Firebase Messaging check failed:', err?.message || err);
      }
    }

    // Analytics is optional and might fail in development / mobile webview
    if (!Capacitor.isNativePlatform()) {
      try {
        if (typeof window !== 'undefined') {
          analytics = getAnalytics(app);
        }
      } catch (analyticsError) {
        analytics = null;
      }
    } else {
      analytics = null;
    }

  } else {
    throw new Error('Invalid Firebase configuration - using demo keys');
  }
} catch (error) {
  console.warn('🔥 Firebase initialization failed:', error.message);
  console.log('📝 Using fallback mode without Firebase');

  app = null;
  db = null;
  auth = null;
  analytics = null;
  storage = null;
  messaging = null;
}

export { db, auth, analytics, storage, messaging };
export default app;
